/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: AI重复照片去重页面
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-07 18:09:54
 */

import React, { useState } from 'react'
import { Layout, message, Button, Modal } from 'antd'
import { PlayCircleOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useSelector } from 'react-redux'
import SelectDetectionTime from './components/SelectDetectionTime'
import DetectionInProgress from './components/DetectionInProgress'
import DetectionCompleted from './components/DetectionCompleted'
import './index.scss'
import ContactUS from '../components/ContactUS'
import contact_us_icon from '../../images/contact_us.png'
import useAITaskEvent from './hooks/useAITaskEvent'
import { useSensorTracking } from './hooks/useSensorTracking'

import PageHeader from '@/components/PageHeader'
// antd 使用 Dayjs 类型替代 RangePickerValue
type RangePickerValue = [Dayjs | null, Dayjs | null] | null
const { Content } = Layout
const MIN_DETECTING_DURATION_MS = 2000

// 检测步骤枚举
enum DetectionStep {
  SELECT_TIME = 'SELECT_TIME', // 选择时间
  PREPARING = 'PREPARING', // 首次开通准备中
  DETECTING = 'DETECTING', // 检测重复照片中
  COMPLETED = 'COMPLETED' // 检测完成
}

const AIDuplicatePhoto: React.FC = () => {
  const [dateRange, setDateRange] = useState<RangePickerValue>([
    dayjs().subtract(1, 'month').startOf('day'),
    dayjs().endOf('day')
  ])
  const [detectionStep, setDetectionStep] = useState<DetectionStep>(
    DetectionStep.SELECT_TIME
  )
  const [photoGroups, setPhotoGroups] = useState<any[]>([])
  const [pageToken, setPageToken] = useState<string>('')
  const [hasMore, setHasMore] = useState<boolean>(false)
  const [loadingMore, setLoadingMore] = useState<boolean>(false)
  /** 首次请求时的参数，用于加载更多时保持一致 */
  const [lastFetchConfig, setLastFetchConfig] = useState<{
    similar_size: number
    chunk_size: number
  } | null>(null)
  /** 是否按时间排序，从配置传入结果页 */
  const [sortByTime, setSortByTime] = useState<boolean>(true)
  /** 首次请求的时间范围，供「显示全部」similar_all 使用 */
  const [fetchTimeRange, setFetchTimeRange] = useState<{
    startTime: number
    endTime: number
  } | null>(null)
  /** 使用演示弹窗是否可见 */
  const [usageDemoVisible, setUsageDemoVisible] = useState(false)

  const ensureMinimumDetectingDuration = async (startedAt: number) => {
    const remaining = MIN_DETECTING_DURATION_MS - (Date.now() - startedAt)
    if (remaining > 0) {
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, remaining)
      })
    }
  }

  // 获取Redux中的groupId
  const groupId = useSelector((state: any) => state.group.groupId)

  // 引入AI任务Hook
  const { syncPhotoSimilarList } = useAITaskEvent()

  // 引入埋点Hook（自动上报进入页面埋点）
  const { trackClick } = useSensorTracking()

  // 处理日期范围变化
  const handleDateRangeChange = (
    dates: RangePickerValue,
    dateStrings: [string, string]
  ) => {
    setDateRange(dates)
  }

  // 处理开始检测
  const handleStartDetection = async (config?: {
    groupsPerPage: number
    photosPerGroup: number
    smartDetection: boolean
    sortByTime?: boolean
  }) => {
    if (
      !dateRange ||
      !Array.isArray(dateRange) ||
      !dateRange[0] ||
      !dateRange[1]
    ) {
      return
    }

    if (!groupId) {
      message.error('未获取到团队ID，请刷新重试')
      return
    }

    // 切换到检测中状态
    setDetectionStep(DetectionStep.DETECTING)
    const detectingStartedAt = Date.now()

    console.log('开始检测', {
      startDate: dateRange[0].format('YYYY-MM-DD'),
      endDate: dateRange[1].format('YYYY-MM-DD'),
      config
    })

    // 调用 similar_list 接口获取数据
    const startTime = dateRange[0].clone().startOf('day').valueOf()
    const endTime = dateRange[1].clone().endOf('day').valueOf()

    try {
      const res = await syncPhotoSimilarList({
        group_id: groupId,
        similar_size: config?.groupsPerPage ?? 10,
        chunk_size: config?.photosPerGroup ?? 12,
        start_time: startTime,
        end_time: endTime,
        page_token: '' // 第一页传空
      })
      await ensureMinimumDetectingDuration(detectingStartedAt)

      if (res.code === 200 && res.data?.status === 200) {
        // 更新数据状态
        if (res.output) {
          setPhotoGroups(res.output.res_list || [])
          setPageToken(res.output.page_token || '')
          setHasMore(res.output.has_more || false)
          setSortByTime(config?.sortByTime ?? true)
          setFetchTimeRange({ startTime, endTime })
          setLastFetchConfig({
            similar_size: config?.groupsPerPage ?? 10,
            chunk_size: config?.photosPerGroup ?? 12
          })
        }
        // 检测完成后切换到完成状态
        setDetectionStep(DetectionStep.COMPLETED)
      } else {
        message.error(res.msg || '检测失败，请稍后重试')
        setDetectionStep(DetectionStep.SELECT_TIME)
      }
    } catch (error) {
      await ensureMinimumDetectingDuration(detectingStartedAt)
      console.error('检测失败:', error)
      message.error('检测过程发生错误，请稍后重试')
      // 发生错误时切回选择时间状态
      setDetectionStep(DetectionStep.SELECT_TIME)
    }
  }

  // 处理返回选择时间
  const handleBackToSelect = () => {
    setDetectionStep(DetectionStep.SELECT_TIME)
  }

  const handleEnterPreparing = () => {
    setDetectionStep(DetectionStep.PREPARING)
  }

  // 用 similar_all 返回的 res_list 追加到指定分组的照片列表
  const handleReplaceGroupPhotos = (similarId: string, photoList: any[]) => {
    setPhotoGroups((prev) =>
      prev.map((g) => {
        if (g.similar_id !== similarId) return g
        // 追加新照片（去重，避免重复添加）
        const existingSeqs = new Set(
          (g.photo_list || []).map((p: any) => p.photo_seq)
        )
        const newPhotos = (photoList || []).filter(
          (p: any) => !existingSeqs.has(p.photo_seq)
        )
        const updatedList = [...(g.photo_list || []), ...newPhotos]
        return {
          ...g,
          photo_list: updatedList,
          max_photo_time:
            updatedList.length > 0
              ? Math.max(...updatedList.map((p: any) => p.photo_time || 0))
              : 0
        }
      })
    )
  }

  // 删除照片后从本地结果中移除对应项（并移除空分组）
  const handlePhotosDeleted = (photoSeqs: number[]) => {
    if (!photoSeqs.length) return
    const set = new Set(photoSeqs)
    setPhotoGroups((prev) =>
      prev
        .map((g) => ({
          ...g,
          photo_list: (g.photo_list || []).filter(
            (p: any) => !set.has(p.photo_seq)
          )
        }))
        .filter((g) => g.photo_list.length > 0)
    )
  }

  // 加载更多相似组（分页）：使用上次返回的 page_token 请求下一页
  const handleLoadMore = async () => {
    if (
      !groupId ||
      !dateRange ||
      !Array.isArray(dateRange) ||
      !dateRange[0] ||
      !dateRange[1] ||
      !lastFetchConfig ||
      loadingMore
    ) {
      return
    }
    const startTime = dateRange[0].clone().startOf('day').valueOf()
    const endTime = dateRange[1].clone().endOf('day').valueOf()
    setLoadingMore(true)
    try {
      const res = await syncPhotoSimilarList({
        group_id: groupId,
        similar_size: lastFetchConfig.similar_size,
        chunk_size: lastFetchConfig.chunk_size,
        start_time: startTime,
        end_time: endTime,
        page_token: pageToken || '' // 上一页返回的 page_token，请求下一页
      })
      if (res.code === 200 && res.data?.status === 200 && res.output) {
        setPhotoGroups((prev) => [...prev, ...(res.output!.res_list || [])])
        setPageToken(res.output.page_token || '')
        setHasMore(res.output.has_more || false)
      } else {
        message.error(res.msg || '加载更多失败')
      }
    } catch (error) {
      console.error('加载更多失败:', error)
      message.error('加载更多失败，请稍后重试')
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <div className="ai-duplicate-photo-page">
      <PageHeader
        ghost={false}
        style={{
          borderBottom: '1px solid rgb(235, 237, 240)'
        }}
        title="AI相似照片去重"
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="default"
              icon={<PlayCircleOutlined />}
              onClick={() => {
                setUsageDemoVisible(true)
                trackClick({ actionType: 'demo' })
              }}
            >
              功能演示
            </Button>
            <ContactUS fromPage="AI相似照片去重">
              <span
                className="contact-item"
                onClick={() => {
                  // sensorsClick('contactUs')
                }}
              >
                <img style={{ width: '105px' }} src={contact_us_icon} alt="" />
              </span>
            </ContactUS>
          </div>
        }
      />
      <Content className="content">
        {detectionStep === DetectionStep.SELECT_TIME && (
          <SelectDetectionTime
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            onStartDetection={handleStartDetection}
            onEnterPreparing={handleEnterPreparing}
          />
        )}
        {detectionStep === DetectionStep.PREPARING && (
          <DetectionInProgress
            variant="notice"
            title="AI相似照片去重权限开通成功"
            statusLabel="准备中"
            descriptionLines={[
              '首次使用预计需要 2-5 分钟准备数据',
              '历史照片不参与检测，系统将从现在起监控新增照片'
            ]}
            primaryActionText="知道了"
            onPrimaryAction={handleBackToSelect}
            showFooter={false}
          />
        )}
        {detectionStep === DetectionStep.DETECTING && <DetectionInProgress />}
        {detectionStep === DetectionStep.COMPLETED && (
          <DetectionCompleted
            onBackToSelect={handleBackToSelect}
            photoGroups={photoGroups}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            sortByTime={sortByTime}
            groupId={groupId}
            fetchTimeRange={fetchTimeRange}
            chunkSize={lastFetchConfig?.chunk_size}
            onReplaceGroupPhotos={handleReplaceGroupPhotos}
            onPhotosDeleted={handlePhotosDeleted}
          />
        )}
      </Content>
      <Modal
        title="功能演示"
        open={usageDemoVisible}
        onCancel={() => setUsageDemoVisible(false)}
        footer={null}
        width={1000}
        styles={{
          body: {
            padding: 0,
            backgroundColor: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '560px',
            position: 'relative'
          }
        }}
        destroyOnHidden
      >
        <video
          src="https://net-cloud.xhey.top/data/cbc81f27-39f6-41f2-8658-d6016fae1bb0.mov"
          controls
          autoPlay
          preload="auto"
          style={{ width: '100%' }}
        />
      </Modal>
    </div>
  )
}

export default AIDuplicatePhoto
